import AudioRecorder from 'simple-audio-recorder'
import { RECORDER_STATES } from './helpers.js'
import { useEffect, useRef } from 'react'
import { PutObjectCommand } from '@aws-sdk/client-s3'
import { useAwsS3Client } from 'Data/s3'
import { useDataValue } from 'Simple/Data.js'
import {
  notifyError,
  notifySuccess,
  useNotifications,
} from 'Logic/Notifications.js'

export default function useDataOnSubmit(props) {
  let s3 = useAwsS3Client(props)
  let parent_company_id = useDataValue({
    context: 'global',
    path: 'current_location.parent_company._id',
    viewPath: props.viewPath,
  })
  let patient_id = useDataValue({
    context: 'patient',
    path: '_id',
    viewPath: props.viewPath,
  })
  let fileBaseKey = `companies/${parent_company_id}/ai-record-poc/patient-${patient_id}`

  let [, notify] = useNotifications()

  /**
   * @type {React.MutableRefObject<{
   *  id: number
   *  chunk: number
   *  chunks: any[]
   *  recorder: {
   *    start: () => void;
   *    stop: () => void;
   *    resume: () => void;
   *    pause: () => void;
   *    ondataavailable: () => void;
   *    onstart: () => void;
   *    onstop: () => void;
   *    onerror: () => void;
   *    getEncodingQueueSize: () => number;
   *  }
   * } | null>}
   */
  let session = useRef(null)

  useEffect(() => {
    AudioRecorder.preload('/mp3worker.js')

    return () => {
      console.log({ type: 'cleanup', recorder: session.current })
      if (session.current === null) return

      session.current.recorder.stop()
    }
  }, [])

  return function onSubmit({ args, value, change }) {
    console.log({ value, args })
    switch (args.type) {
      case 'START': {
        if (session.current !== null) {
          session.current.recorder.stop()
        }

        change(next => {
          next.state = RECORDER_STATES.STARTING
          next.error = null
        })

        session.current = {
          id: Date.now(),
          chunk: 0,
          chunks: [],
          recorder: new AudioRecorder({
            // Initial recording volume
            recordingGain: 1,
            // MP3 encoding bit rate
            encoderBitRate: 320,
            // Data will be returned in chunks (ondataavailable callback) as it is encoded,
            streaming: true,
            // Size of encoded mp3 data chunks returned by ondataavailable, if streaming is enabled
            // rather than at the end as one large blob
            streamBufferSize: 250_000,
            constraints: {
              deviceId: value.deviceId,
              // Optional audio constraints, see https://developer.mozilla.org/en-US/docs/Web/API/MediaDevices/getUserMedia
              // Set to 2 to hint for stereo if it's available, or leave as 1 to force mono at all times
              channelCount: 2,
              autoGainControl: true,
              echoCancellation: true,
              noiseSuppression: true,
            },
          }),
        }

        session.current.recorder.ondataavailable = async data => {
          if (session.current === null) return

          session.current.chunks.push(data)

          let _s3 = await s3()
          if (_s3 === null) return

          await _s3.send(
            new PutObjectCommand({
              Bucket: process.env.REACT_APP_S3_BUCKET_FILES,
              Key: `${fileBaseKey}-session-${session.current.id}-chunk-${session
                .current.chunk++}.mp3`,
              Body: new Blob([data], { type: 'audio/mpeg' }),
              ContentType: 'audio/mpeg',
            })
          )
        }

        session.current.recorder.onstart = () => {
          change(next => {
            next.state = RECORDER_STATES.RECORDING
          })
        }

        session.current.recorder.onstop = async () => {
          change(next => {
            next.state = RECORDER_STATES.COMPLETE
          })

          if (session.current === null) return

          try {
            let _s3 = await s3()
            if (_s3 === null) return

            await _s3.send(
              new PutObjectCommand({
                Bucket: process.env.REACT_APP_S3_BUCKET_FILES,
                Key: `${fileBaseKey}-session-${session.current.id}.mp3`,
                Body: new Blob(session.current.chunks, { type: 'audio/mpeg' }),
                ContentType: 'audio/mpeg',
              })
            )

            notify(notifySuccess('Recording uploaded successfully!'))
          } catch (error) {
            notify(
              notifyError(
                `Recording failed to upload. Session ID ${session.current.id}`
              )
            )
            console.error({
              type: 'recorder/failed-upload',
              error,
              fileBaseKey,
              sessionId: session.current.id,
            })
          }

          session.current = null
        }

        session.current.recorder.onerror = error => {
          change(next => {
            next.state = RECORDER_STATES.ERROR
            next.error = error
          })
        }

        session.current.recorder.start()
        return
      }

      case 'STOP': {
        if (session.current === null) return
        if (
          value.state !== RECORDER_STATES.RECORDING &&
          value.state !== RECORDER_STATES.PAUSED
        )
          return

        if (session.current.recorder.getEncodingQueueSize() > 1000) {
          // If there's a fair amount of data left, we'll enter the ENCODING state.
          // (so a spinner or something could be shown)
          change(next => {
            next.state = RECORDER_STATES.ENCODING
          })
        }

        session.current.recorder.stop()
        return
      }

      case 'PAUSE': {
        if (session.current === null) return
        if (value.state !== RECORDER_STATES.RECORDING) return

        session.current.recorder.pause()
        change(next => {
          next.state = RECORDER_STATES.PAUSED
        })
        return
      }

      case 'RESUME': {
        if (session.current === null) return
        if (value.state !== RECORDER_STATES.PAUSED) return

        session.current.recorder.resume()
        change(next => {
          next.state = RECORDER_STATES.RECORDING
        })
        return
      }

      default: {
        return
      }
    }
  }
}
